import * as R from "ramda";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createConnectCard, getConnectorTables, getConnectors } from "../api";
import {
  deleteConnector as deleteConnectorApi,
  getConnectorConfigUrl,
} from "../api/fivetran";
import { useAppId, useSelectedPage } from "../hook";
import {
  ActionType,
  AppDispatch,
  PageContentType,
  RootState,
} from "../reducer";
import {
  AddPullConnectorStatusAction,
  AddPullConnectorUrlConfigsAction,
  SetSelectedPullConnectorTableAction,
} from "../reducer/pullConnector";
import {
  ConnectCard,
  Connector,
  ConnectorStatus,
  ConnectorStatusMap,
  ConnectorType,
  ConnectorUrlConfig,
} from "../types/pullConnector";
import { Table } from "../types/table";
import { useAppIdMap } from "./app";
import { useSelectedNotebookPageId } from "./page";
import { useAddTables, useTable } from "./table";

export const useAssociateConnectorTables: () => (
  connectorTables: any
) => void = () => {
  const dispatch = useDispatch();
  const associateConnectorTables = (connectorTables: any) => {
    dispatch({ type: ActionType.ASSOCIATE_CONNECTOR_TABLES, connectorTables });
    // const connectorId = Object.keys(connectorTables)[0];
    // const tableId = connectorTables[connectorId][0]["file-id"];
    // setSelectedPullConnector({ connectorId, tableId });
  };
  return associateConnectorTables;
};

export const useConnectorStatusMap: () => [
  ConnectorStatusMap,
  (connectorStatusMap: ConnectorStatusMap) => void
] = () => {
  const dispatch = useDispatch();
  const connectorStatusMap = useSelector(
    (state: RootState) => state.connectors.connectorStatusMap
  );
  const setConnectorStatusMap = (connectorStatusMap: ConnectorStatusMap) => {
    dispatch({ type: ActionType.SET_CONNECTOR_STATUS_MAP, connectorStatusMap });
  };
  return [connectorStatusMap, setConnectorStatusMap];
};

export const useGetConnectorStatus: () => (
  connectorId: string
) => ConnectorStatus | undefined = () => {
  const [connectorStatusMap, setConnectorStatusMap] = useConnectorStatusMap();
  return (connectorId: string) => {
    return connectorStatusMap[connectorId];
  };
};

export const useConnectorStatus: (
  connectorId?: string
) => ConnectorStatus | undefined = (connectorId) => {
  const [statusMap] = useConnectorStatusMap();
  return connectorId ? statusMap[connectorId] : undefined;
};

export const useAddConnectorStatus: () => (
  connectorId: string,
  status: any
) => void = () => {
  const dispatch = useDispatch();

  return (connectorId, status) => {
    const action: AddPullConnectorStatusAction = {
      type: ActionType.ADD_PULL_CONNECTOR_STATUS,
      connectorId,
      status,
    };
    dispatch(action as any);
  };
};

export const useFetchConnectorPageData: () => (
  connectorId: string
) => void = () => {
  const [appId] = useAppId();
  const addConnectorStatus = useAddConnectorStatus();
  const addTables = useAddTables();
  const associateConnectorTables = useAssociateConnectorTables();

  return (connectorId) => {
    getConnectorTables({ appId: appId!, connectorId }).then((response) => {
      const connectorData = { [response["connector-id"]]: response.tables };
      addConnectorStatus(response["connector-id"], response.status);
      associateConnectorTables(connectorData);
      addTables(response.tables);
    });
  };
};

export const useHandleConnectorsResponse: () => (
  connectorsResponse: any
) => void = () => {
  const [connectors, setConnectors] = useConnectors();
  const [selectedPullConnector, setSelectedPullConnector] =
    useSelectedPullConnector();

  const handleConnectorsResponse = (connectorsResponse: any) => {
    const cards = R.sortBy(
      R.path(["card", "schema"]) as any,
      R.prop("cards", connectorsResponse)
    ) as any[];
    // setSelectedPullConnector("receiving_insofar");
    setConnectors(cards);
  };
  return handleConnectorsResponse;
};

export const useConnectorTables: (connectorId: string) => Table[] = (
  connectorId
) => {
  const tables = useSelector(
    (state: RootState) => state.ui.connectorTables?.[connectorId] ?? []
  );
  return tables;
};

export const useSelectedPullConnector: () => [
  string | undefined,
  (connectorId: string) => void
] = () => {
  const [selectedPage, setSelectedPage] = useSelectedPage();
  const dispatch = useDispatch();
  const selectedPullConnector = useSelector(
    (state: RootState) => state.ui.selectedPullConnector
  );

  const setSelectedPullConnector = (selectedPullConnector: string) => {
    dispatch({
      type: ActionType.SET_SELECTED_PULL_CONNECTOR,
      selectedPullConnector,
    });
    setSelectedPage({ contentType: PageContentType.PULL_CONNECTOR });
  };

  return [selectedPullConnector, setSelectedPullConnector];
};

export const useDeletePullConnector: () => (config: {
  connectorId: string;
}) => void = () => {
  const [appId, setAppId] = useAppId();
  const handleConnectorsResponse = useHandleConnectorsResponse();
  const [selectedPage, setSelectedPage] = useSelectedNotebookPageId();

  const deletePullConnector: (config: { connectorId: string }) => void = ({
    connectorId,
  }) => {
    setSelectedPage(undefined);
    deleteConnectorApi({ appId: appId!, connectorId }).then(() => {
      getConnectors({ appId: appId! }).then((connectorsResponse) => {
        handleConnectorsResponse(connectorsResponse);
      });
    });
  };

  return deletePullConnector;
};

export const useConnectors: () => [
  Connector[],
  (connectors: Connector[]) => void
] = () => {
  const connectors = useSelector((state: RootState) => state.connectors.connectors);
  const dispatch = useDispatch<AppDispatch>();
  const setConnectors = (connectors: Connector[]) =>
    dispatch({ type: ActionType.SET_CONNECTORS, connectors });
  return [connectors, setConnectors];
};

export const useGetConnector: () => (
  connectorId: string | undefined
) => Connector | undefined = () => {
  const [connectors, setConnectors] = useConnectors();
  return (connectorId: string | undefined) => {
    return connectors.find(
      (connector) => connector["connection-id"] === connectorId
    );
  };
};

export const useConnectorTypes: () => [
  ConnectorType[],
  (connectorTypes: ConnectorType[]) => void
] = () => {
  const connectorTypes = useSelector(
    (state: RootState) => state.connectors.connectorTypes
  );
  const dispatch = useDispatch<AppDispatch>();
  const setConnectorTypes = (connectorTypes: ConnectorType[]) =>
    dispatch({ type: ActionType.SET_CONNECTOR_TYPES, connectorTypes });
  return [connectorTypes, setConnectorTypes];
};

export const useSelectedConnectorTypeRouterParamListener = () => {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const connectorTypeName = searchParams.get("connectorTypeName");
  const [connectorTypes] = useConnectorTypes();
  const { setSelectedConnectorType } = useSelectedConnectorType();

  useEffect(() => {
    if (!connectorTypes) return;
    const connectorType = connectorTypes.find(
      (conType) => conType.name === connectorTypeName
    );
    setSelectedConnectorType(connectorType);
  }, [connectorTypeName, connectorTypes]);
};

export const useSelectedConnectorType = () => {
  const selectedConnectorType = useSelector(
    (state: RootState) => state.ui.selectedConnectorType
  );
  const [selectedPage, setSelectedPage] = useSelectedPage();
  const dispatch = useDispatch<AppDispatch>();
  const setSelectedConnectorType = (selectedConnectorType: any) => {
    dispatch({
      type: ActionType.SET_SELECTED_CONNECTOR_TYPE,
      selectedConnectorType,
    });
  };
  return { selectedConnectorType, setSelectedConnectorType };
};

export const useSelectedConnectCard = () => {
  const selectedConnectCard = useSelector(
    (state: RootState) => state.ui.selectedConnectCard
  );
  const dispatch = useDispatch();
  const setSelectedConnectCard = (selectedConnectCard: ConnectCard) => {
    dispatch({
      type: ActionType.SET_SELECTED_CONNECT_CARD,
      selectedConnectCard,
    });
  };
  return { selectedConnectCard, setSelectedConnectCard };
};

export const useAddConnectCardUrl = (connectorTypeName: string) => {
  const { appId } = useAppIdMap();
  const queryString = new URLSearchParams({ connectorTypeName }).toString();
  return `/app/${appId}/app-connector/add?${queryString}`;
};

export const useFetchSelectedConnectCardListener: () => any = () => {
  const { selectedConnectorType } = useSelectedConnectorType();
  const { selectedConnectCard, setSelectedConnectCard } =
    useSelectedConnectCard();
  const selectedConnectorTypeId = selectedConnectorType?.id;
  const selectedConnectCardId = selectedConnectCard?.id;
  const [appId, setAppId] = useAppId();

  useEffect(() => {
    if (selectedConnectCardId || !selectedConnectorTypeId || !appId) return;
    createConnectCard({
      appId: appId!,
      schema: selectedConnectorTypeId,
    }).then((response) => {
      const card = response["connect-card"];
      setSelectedConnectCard(card);
    });
  }, [selectedConnectCardId, selectedConnectorTypeId, appId]);
};

export const useNavigateToSelectedConnectCardListener = () => {
  const { selectedConnectCard } = useSelectedConnectCard();
  const pullConnectorPath = usePullConnectorPath();
  const path = pullConnectorPath(selectedConnectCard?.id);
  const navigate = useNavigate();

  useEffect(() => {
    if (!path) return;
    navigate(path);
  }, [path]);
};

export const useFetchConnectorUrl: () => (
  connectorId: string
) => Promise<ConnectorUrlConfig> = () => {
  const dispatch = useDispatch<AppDispatch>();

  return async (connectorId) => {
    return getConnectorConfigUrl({ connectorId }).then((res) => {
      const action: AddPullConnectorUrlConfigsAction = {
        type: ActionType.ADD_PULL_CONNECTOR_URL_CONFIGS,
        configs: [res],
      };
      dispatch(action as any);
      return res;
    });
  };
};

export const useConnectorConfigUrl: (
  connectorId?: string
) => string | undefined = (connectorId) => {
  const url = useSelector((state: RootState) =>
    connectorId ? state.connectors.connectorConfigUrls[connectorId] : undefined
  );
  return url;
};

export const usePullConnectorRouteParamListener: () => void = () => {
  const { "pull-connector-id": pullConnectorIdParam } = useParams();
  const [selectedPullConnectorId, setSelectedPullConnectorId] =
    useSelectedPullConnector();
  const [appId] = useAppId();
  useEffect(() => {
    if (!appId) return;
    if (!pullConnectorIdParam) return;
    if (selectedPullConnectorId !== pullConnectorIdParam) {
      setSelectedPullConnectorId(pullConnectorIdParam);
    }
  }, [appId, pullConnectorIdParam, selectedPullConnectorId]);
};

export const usePullConnectorTableRouteParamListener: () => void = () => {
  const { "table-id": tableIdParam } = useParams();
  const { setSelectedPullConnectorTableId } = useSelectedPullConnectorTable();
  useEffect(() => {
    if (!tableIdParam) return;
    setSelectedPullConnectorTableId(tableIdParam);
  }, [tableIdParam]);
};

export const usePullConnectorPath: () => (
  pullConnectorId: string | undefined
) => string = () => {
  const [appId] = useAppId();

  return (pullConnectorId) => {
    if (!appId) return "";
    return `/app/${appId}/app-connector/${pullConnectorId}`;
  };
};

export const useSelectedPullConnectorTable = () => {
  const selectedPullConnectorTableId = useSelector(
    (state: RootState) => state.connectors.selectedPullConnectorTableId
  );
  const dispatch = useDispatch<AppDispatch>();
  const setSelectedPullConnectorTableId = (
    selectedPullConnectorTableId: string | undefined
  ) => {
    const action: SetSelectedPullConnectorTableAction = {
      type: ActionType.SET_SELECTED_PULL_CONNECTOR_TABLE,
      selectedPullConnectorTableId,
    };
    dispatch(action as any);
  };
  const selectedPullConnectorTable = useTable(selectedPullConnectorTableId);

  return {
    selectedPullConnectorTableId,
    setSelectedPullConnectorTableId,
    selectedPullConnectorTable,
  };
};

export const usePullConnectorTablePath: () => (config: {
  pullConnectorId: string;
  tableId: string;
}) => string = () => {
  const [appId] = useAppId();
  return ({ pullConnectorId, tableId }) => {
    return `/app/${appId}/app-connector/${pullConnectorId}/table/${tableId}`;
  };
};

export const useAddConnectorPath: () => string = () => {
  const [appId] = useAppId();
  return `/app/${appId}/app-connector/select-type`;
};

export const useFetchPullConnectorListener = () => {
  const [selectedPullConnector, setSelectedPullConnector] =
    useSelectedPullConnector();
  const fetchConnectorPageData = useFetchConnectorPageData();

  useEffect(() => {
    if (!selectedPullConnector) return;
    fetchConnectorPageData(selectedPullConnector!);
  }, [selectedPullConnector]);
};

export const useFetchPullConnectorUrlListener = () => {
  const [selectedPullConnector, setSelectedPullConnector] =
    useSelectedPullConnector();
  const fetchConnectorUrl = useFetchConnectorUrl();

  useEffect(() => {
    if (!selectedPullConnector) return;
    fetchConnectorUrl(selectedPullConnector!);
  }, [selectedPullConnector]);
};
